import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import {
  CityMatrix,
  ServiceFirstIndex,
  type IndexCity,
  type IndexService,
} from "@/components/city-index/CityIndexSections";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { LOCATIONS } from "@/content/locations";
import { SERVICES } from "@/content/services";
import { PROGRAMMABLE_SERVICE_SLUGS } from "@/content/city-services";
import { isPairedCity } from "@/content/language-pairs";
import { buildSeo } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";
import { HUB_FAQS_EN } from "@/content/hub-faqs";
import { SITE } from "@/content/site";
import { FaqCardGrid } from "@/components/FaqCardGrid";

const PROGRAMMABLE_SERVICES = PROGRAMMABLE_SERVICE_SLUGS.map((slug) => {
  const svc = SERVICES.find((s) => s.slug === slug)!;
  return { slug, title: svc.title, tagline: svc.tagline };
});

/** Short, scannable labels for the matrix (full service titles are verbose). */
const SHORT_LABEL: Record<string, string> = {
  "local-seo": "Local SEO",
  "technical-seo-audit": "Technical SEO Audit",
  "ecommerce-seo": "Ecommerce SEO",
  "aeo-geo": "AEO & GEO (AI Search)",
  "content-strategy": "Content Strategy",
};

const TOTAL_PAGES = LOCATIONS.length * PROGRAMMABLE_SERVICES.length;

const INDEX_SERVICES: IndexService[] = PROGRAMMABLE_SERVICES.map((s) => ({
  slug: s.slug,
  title: SHORT_LABEL[s.slug],
  tagline: s.tagline,
  search: [SHORT_LABEL[s.slug], s.title],
}));

const INDEX_CITIES: IndexCity[] = LOCATIONS.map((l) => ({
  slug: l.slug,
  name: l.city,
  region: l.region,
  district: l.district,
  industries: l.industries,
  search: [l.city, l.region, l.district],
  paired: isPairedCity(l.slug),
}));

export const Route = createFileRoute("/seo-services/")({
  head: () =>
    buildSeo({
      title: `SEO Services Index — Every City × Service in Bangladesh | ${SITE.name}`,
      description: `Complete index of ${TOTAL_PAGES}+ location pages: local SEO, technical audits, ecommerce SEO, AEO/GEO and content strategy for ${LOCATIONS.length} cities across Bangladesh.`,
      path: "/seo-services",
      ogEyebrow: "Service Index",
      alternates: { bn: "/bn/seo-services" },
      keywords:
        "SEO services index Bangladesh, local SEO by city, technical SEO audit Bangladesh, ecommerce SEO city, AEO GEO Bangladesh",
    }),
  component: ServiceLocationIndex,
});

function ServiceLocationIndex() {
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "SEO services by city and specialism — Bangladesh",
    numberOfItems: TOTAL_PAGES,
    itemListElement: LOCATIONS.flatMap((l, ci) =>
      PROGRAMMABLE_SERVICES.map((s, si) => ({
        "@type": "ListItem",
        position: ci * PROGRAMMABLE_SERVICES.length + si + 1,
        url: `${SITE.url}/seo-services/${l.slug}/${s.slug}`,
        name: `${SHORT_LABEL[s.slug]} in ${l.city}`,
      })),
    ),
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "SEO Locations", path: "/seo-locations" },
          { name: "Service Index", path: "/seo-services" },
        ]}
      />
      <JsonLd data={itemList} />
      <JsonLd data={faqSchema(HUB_FAQS_EN)} />

      <h1 className="sr-only">
        SEO services index — every city and service page in Bangladesh
      </h1>

      <Section
        eyebrow="Complete index"
        title={
          <>
            Every city, every service —{" "}
            <span className="text-gradient">one index.</span>
          </>
        }
        subtitle={`${TOTAL_PAGES} dedicated pages across ${LOCATIONS.length} Bangladeshi cities and ${PROGRAMMABLE_SERVICES.length} specialisms. Find the exact page for your market and the work you need.`}
      >
        <div className="grid lg:grid-cols-3 gap-8 -mt-4">
          <div className="lg:col-span-2 space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>
              Each combination below is a real page with market-specific
              research: local competitors, neighborhoods, the industries that
              actually search in that city, and the deliverables I ship there.
              Nothing is a template swap of the city name.
            </p>
            <p>
              Start with your city if you know it, or start with the service if
              you're comparing approaches. Both routes land in the same place.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-sm text-muted-foreground">Index size</div>
            <div className="font-display text-4xl mt-1">{TOTAL_PAGES}</div>
            <div className="text-sm text-muted-foreground mt-1">
              city × service pages
            </div>
            <div className="mt-5 flex flex-col gap-2 text-sm">
              <Link to="/seo-locations" className="text-primary hover:text-foreground">
                Browse city hubs →
              </Link>
              <Link to="/services" className="text-primary hover:text-foreground">
                Browse all services →
              </Link>
              <Link to="/sitemap" className="text-primary hover:text-foreground">
                Full HTML sitemap →
              </Link>
              <Link to="/bn/seo-services" hrefLang="bn" className="text-primary hover:text-foreground">
                বাংলা শহর × সেবা ইনডেক্স →
              </Link>
            </div>
          </div>
        </div>

        <ServiceFirstIndex lang="en" services={INDEX_SERVICES} cities={INDEX_CITIES} />
      </Section>

      {/* CITY-FIRST MATRIX */}
      <Section
        eyebrow="Browse by city"
        title={<>The full city × service matrix</>}
        className="pt-0"
      >
        <CityMatrix
          lang="en"
          cities={INDEX_CITIES}
          services={INDEX_SERVICES}
          sortRegions
          hubLabel={(l) => <>SEO in {l.name}</>}
          serviceLabel={(l, s) => (
            <>
              {s.title} in {l.name}
            </>
          )}
        />
      </Section>
      <Section
        eyebrow="Index FAQ"
        title={<>Before you pick a page — <span className="text-gradient">common questions.</span></>}
        className="pt-0"
      >
        <FaqCardGrid items={HUB_FAQS_EN} />
      </Section>
    </>
  );
}
